import React from "react"
import { Button, Card, CardBody, Table } from "reactstrap"
import { useTranslation } from "react-i18next"

import Paginations from "components/Common/Pagination"
import NoDataAvailable from "components/Shared/NoDataAvailable"
import MarkIDCardPrintedModal from "./MarkIDCardPrintedModal"
import { ListChecks } from "lucide-react"
import ChangeAssetsStatusModal from "./ChangeAssetsStatusModal"
import { useDispatch } from "react-redux"
import { generateSingleIDCard } from "../../../store/actions/cardsAction"
import { showAlert } from "../../../store/reducers/alertReducer"

const IDCardsTable = ({
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
  loading,
  data = [],
  payload,
  activeTab,
  trigger,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const [showMarkPrintedModal, setShowMarkPrintedModal] = React.useState(false)
  const [showChangeAssetsStatusModal, setShowChangeAssetsStatusModal] =
    React.useState(false)
  const [selected, setSelected] = React.useState(null)
  const headers = [
    "S. No.",
    "ID Card No.",
    "National ID",
    "First Name",
    "Middle Name",
    "Last Name",
    "Email",
    "Phone Number",
    "Job Title",
    "ID Card Status",
    "Uniform Status",
    "Action",
  ]

  const generateSingleIdCardPDF = async uuid => {
    const payload = {
      id_card_uuid: uuid,
    }

    dispatch(generateSingleIDCard(payload))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload || {}
        if (statusCode === 200 && success && data) {
          dispatch(showAlert({ text: message, type: "success" }))
          const fileURL = data
          window.open(fileURL, "_blank")
          trigger()
        } else {
          dispatch(
            showAlert({ text: message || "An error occurred", type: "error" })
          )
        }
      })
      .catch(err => {
        console.error("Submission error:", err)
        dispatch(showAlert({ text: "Unexpected error", type: "error" }))
      })
  }

  return (
    <>
      <Card className="user_personal_info theme_card overflow-x-auto">
        <CardBody>
          <div className="table-responsive">
            <Table className="table mb-0">
              <thead className="table-light full_th">
                <tr>
                  {headers.map((item, index) => (
                    <th key={index}>{t(item)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={index}>
                    <th scope="row">
                      {(currentPage - 1) * (payload.limit || 50) + index + 1}
                    </th>
                    <td>
                      <p
                        className="text-decoration-underline cursor-pointer"
                        onClick={() => {
                          if (item.card_path) {
                            window.open(item.card_path, "_blank")
                          } else {
                            generateSingleIdCardPDF(item.uuid)
                          }
                        }}
                      >
                        {item.id_card_serial_number}
                      </p>
                    </td>
                    <td>{item.national_id}</td>
                    <td>
                      {language === "en"
                        ? item.first_name || "-"
                        : item.first_name_ar || "-"}
                    </td>
                    <td>
                      {language === "en"
                        ? item.middle_name || "-"
                        : item.middle_name_ar || "-"}
                    </td>
                    <td>
                      {language === "en"
                        ? item.last_name || "-"
                        : item.last_name_ar || "-"}
                    </td>
                    <td>{item.email}</td>
                    <td>{item.phone_number}</td>
                    <td className="fit_content">{item.job_title || "-"}</td>
                    <td>
                      {item.id_card_status === 0
                        ? t("Generated")
                        : item.id_card_status === 1
                        ? t("Printed")
                        : item.id_card_status === 2
                        ? t("Dispatched")
                        : item.id_card_status === 3
                        ? t("Confirmed")
                        : t("Pending")}
                    </td>
                    <td>
                      {item.uniform_status === 0
                        ? t("Generated")
                        : item.uniform_status === 1
                        ? t("Printed")
                        : item.uniform_status === 2
                        ? t("Dispatched")
                        : item.uniform_status === 3
                        ? t("Confirmed")
                        : t("Pending")}
                    </td>
                    <td>
                      <div className="d-flex gap-2 action_buttons">
                        <Button
                          color="primary"
                          onClick={() => {
                            setSelected(item)
                            if (activeTab === "generated") {
                              setShowMarkPrintedModal(true)
                            } else {
                              setShowChangeAssetsStatusModal(true)
                            }
                          }}
                          disabled={
                            (item.id_card_status === 2 &&
                              item.uniform_status === 2) ||
                            (item.id_card_status === 2 &&
                              item.uniform_status === 3) ||
                            (item.id_card_status === 3 &&
                              item.uniform_status === 2) ||
                            (item.id_card_status === 3 &&
                              item.uniform_status === 3)
                          }
                          title={t("Change Status")}
                        >
                          <ListChecks />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
        {!loading && data.length === 0 && <NoDataAvailable />}
      </Card>
      {!loading && data.length > 0 && (
        <Paginations
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          onPageChange={onPageChange}
        />
      )}
      {showMarkPrintedModal && (
        <MarkIDCardPrintedModal
          isOpen={showMarkPrintedModal}
          toggle={() => {
            setSelected(null)
            setShowMarkPrintedModal(false)
          }}
          trigger={() => trigger()}
          selectedBatch={selected}
          activeTab={activeTab}
        />
      )}
      {showChangeAssetsStatusModal && (
        <ChangeAssetsStatusModal
          isOpen={showChangeAssetsStatusModal}
          toggle={() => {
            setSelected(null)
            setShowChangeAssetsStatusModal(false)
          }}
          trigger={() => trigger()}
          selected={selected}
          activeTab={activeTab}
        />
      )}
    </>
  )
}

export default IDCardsTable
